// Local Blog Service for Development Mode
// This service reads blog data directly from the public/BlogData folder
// instead of hitting Firebase emulator or cloud functions

// Fetch blog data from public folder via HTTP
const fetchBlogData = async (path) => {
  try {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching blog data from ${path}:`, error);
    throw error;
  }
};

// Fetch markdown content from public folder
const fetchMarkdownData = async (path) => {
  try {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${path}: ${response.status} ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    console.error(`Error fetching markdown data from ${path}:`, error);
    throw error;
  }
};

// Parse markdown frontmatter and content
const parseMarkdown = (markdownText) => {
  const frontmatterRegex = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/;
  const match = markdownText.match(frontmatterRegex);
  
  if (!match) {
    throw new Error('Invalid markdown format - missing frontmatter');
  }
  
  const [, frontmatterText, content] = match;
  const frontmatter = {};
  
  // Parse frontmatter
  frontmatterText.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();
      
      // Remove quotes if present
      if ((value.startsWith('"') && value.endsWith('"')) || 
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      
      // Parse arrays
      if (value.startsWith('[') && value.endsWith(']')) {
        try {
          value = JSON.parse(value);
        } catch (e) {
          // If JSON parsing fails, keep as string
        }
      }
      
      frontmatter[key] = value;
    }
  });
  
  return {
    ...frontmatter,
    content: content.trim()
  };
};

// Process image URLs for local development
const processLocalImageUrls = (content) => {
  if (!content) return content;
  
  // If it's an array (content blocks), process each item
  if (Array.isArray(content)) {
    return content.map(item => processLocalImageUrls(item));
  }
  
  // If it's an object, process its properties
  if (typeof content === 'object') {
    const processed = { ...content };
    
    // Process image src in content blocks
    if (processed.type === 'image' && processed.src) {
      // Convert gs:// URLs to local paths for development
      if (processed.src.startsWith('gs://')) {
        // Extract the path after the bucket name
        const pathParts = processed.src.split('/');
        const blogPath = pathParts.slice(4).join('/'); // Skip gs://, bucket, Blog
        // URL encode the path to handle spaces and special characters
        const encodedPath = blogPath.split('/').map(part => encodeURIComponent(part)).join('/');
        processed.src = `/BlogData/${encodedPath}`;
      }
    }
    
    // Process landscapeImage in blog list items
    if (processed.landscapeImage && processed.landscapeImage.startsWith('gs://')) {
      const pathParts = processed.landscapeImage.split('/');
      const blogPath = pathParts.slice(4).join('/'); // Skip gs://, bucket, Blog
      // URL encode the path to handle spaces and special characters
      const encodedPath = blogPath.split('/').map(part => encodeURIComponent(part)).join('/');
      processed.landscapeImage = `/BlogData/${encodedPath}`;
    }
    
    // Recursively process nested content
    if (processed.content) {
      processed.content = processLocalImageUrls(processed.content);
    }
    
    return processed;
  }
  
  return content;
};

// Local Blog Service Functions
export const localBlogService = {
  // Get blog list from public/BlogData/BlogList.json
  getBlogList: async () => {
    try {

      
      // Fetch blog list from public folder
      const blogList = await fetchBlogData('/BlogData/BlogList.json');
      
      // Process image URLs for local development
      const processedBlogs = blogList.map(blog => processLocalImageUrls(blog));
      
      return {
        status: 'success',
        data: processedBlogs
      };
    } catch (error) {
      console.error('Error loading local blog list:', error);
      return {
        status: 'error',
        message: 'Failed to load local blog list'
      };
    }
  },

  // Get specific blog content from public/BlogData/{id}/{id}.md or {id}.json
  getBlogContent: async (blogId) => {
    try {
      // Try to fetch markdown first, then fall back to JSON
      let blogContent;
      try {
        const markdownText = await fetchMarkdownData(`/BlogData/${blogId}/${blogId}.md`);
        blogContent = parseMarkdown(markdownText);
        // Process markdown images (convert to local paths)
        blogContent.content = blogContent.content.replace(
          /!\[([^\]]*)\]\(\/BlogData\/([^)]+)\)/g,
          (match, alt, imagePath) => {
            // Check if the path is already encoded (contains %20 or other encoded chars)
            const isAlreadyEncoded = imagePath.includes('%');
            if (isAlreadyEncoded) {
              // Path is already encoded, use as-is
              return `![${alt}](/BlogData/${imagePath})`;
            } else {
              // Path needs encoding
              const encodedPath = imagePath.split('/').map(part => encodeURIComponent(part)).join('/');
              return `![${alt}](/BlogData/${encodedPath})`;
            }
          }
        );
      } catch (mdError) {
        // Fall back to JSON format
        blogContent = await fetchBlogData(`/BlogData/${blogId}/${blogId}.json`);
        // Process image URLs for local development (JSON format)
        blogContent = processLocalImageUrls(blogContent);
      }
      
      return {
        status: 'success',
        data: blogContent
      };
    } catch (error) {
      console.error(`Error loading local blog content for ID ${blogId}:`, error);
      return {
        status: 'error',
        message: `Failed to load blog content for ID ${blogId}`
      };
    }
  },

  // Get featured blog from public/BlogData/BlogList.json
  getFeaturedBlog: async () => {
    try {
      console.log('📁 Loading featured blog from public/BlogData folder...');
      
      // Fetch blog list from public folder
      const blogList = await fetchBlogData('/BlogData/BlogList.json');
      
      // Find the featured blog
      const featuredBlog = blogList.find(blog => blog.featured === true);
      
      if (!featuredBlog) {
        return {
          status: 'error',
          message: 'No featured blog found'
        };
      }
      
      // Process image URLs for local development
      const processedBlog = processLocalImageUrls(featuredBlog);
      
      return {
        status: 'success',
        data: processedBlog
      };
    } catch (error) {
      console.error('Error loading local featured blog:', error);
      return {
        status: 'error',
        message: 'Failed to load featured blog'
      };
    }
  }
};

// Environment detection
export const isLocalDevelopment = () => {
  // Check if explicitly enabled
  if (process.env.REACT_APP_USE_LOCAL_BLOGS === 'true') {
    return true;
  }
  
  // Check if in development mode with local GCF URL
  return process.env.NODE_ENV === 'development' && 
         (!process.env.REACT_APP_GCF_BASE_URL ||
          process.env.REACT_APP_GCF_BASE_URL.includes('localhost') ||
          process.env.REACT_APP_GCF_BASE_URL.includes('127.0.0.1'));
};

export default localBlogService;
